/**
 * @file Fixed pill at the top of the screen showing the exam session label.
 * Includes tamper detection: if the DOM content is modified (for example via DevTools), a
 * MutationObserver forces a re-render to restore it.
 */
import moment from 'moment';
import p from 'vue-strict-prop';
import * as tsx from 'vue-tsx-support';
import { UserStore } from '@/store';
import { datetime } from '@/utils';
import './style.less';
/**
 * How often to verify the user's login session with the backend. Must be less
 * than ANIMATION_CYCLE × ANIMATION_EXPIRY_CYCLES, otherwise the animation
 * would expire before the next check has a chance to renew it.
 */
const LOGIN_CHECK_INTERVAL = moment.duration(10, 's');
/** How often to check whether the pill DOM content was tampered with. */
const TAMPER_RECHECK_INTERVAL = moment.duration(1, 'm');
/**
 * Duration of a single shimmer animation cycle. Independent of
 * LOGIN_CHECK_INTERVAL — the animation may be renewed mid-cycle.
 */
const ANIMATION_CYCLE = moment.duration(12, 's');
/**
 * Number of full animation cycles before the session is considered unverified.
 * Total grace period is ANIMATION_CYCLE × ANIMATION_EXPIRY_CYCLES. The
 * animation naturally expires after this many cycles; if timers are cleared by
 * a student, no renewal happens and the shimmer stops on its own.
 */
const ANIMATION_EXPIRY_CYCLES = 3;
export default tsx.component({
    name: 'exam-session-pill',
    props: {
        /** The label to display. */
        label: p(String).required,
    },
    /** @returns The initial data. */
    data() {
        return {
            tamperCount: 0,
            observer: undefined,
            rerenderTimer: undefined,
            animation: undefined,
            destroyed: false,
            lastLoginCheck: moment.utc(),
        };
    },
    /** Set up tamper detection after each render. */
    mounted() {
        this.renewAnimation();
        this.startObserver();
        this.rerenderTimer = setInterval(() => this.restoreIfTampered(), TAMPER_RECHECK_INTERVAL.asMilliseconds());
        this.startVerifyLoginLoop();
    },
    /** Restart observer after re-render. */
    updated() {
        this.startObserver();
    },
    /** Clean up. */
    beforeDestroy() {
        this.animation?.cancel();
        this.observer?.disconnect();
        if (this.rerenderTimer != null) {
            clearInterval(this.rerenderTimer);
        }
        this.destroyed = true;
    },
    computed: {
        /** @returns The label, with an unverified suffix when the animation has expired. */
        fullLabel() {
            if (this.animation == null) {
                return `${this.label} (Not Verified)`;
            }
            return this.label;
        },
    },
    methods: {
        /** Start a recurring loop that verifies the user's login session with the backend. */
        startVerifyLoginLoop() {
            /** Run a single verification and schedule the next one. */
            const check = async () => {
                if (this.destroyed) {
                    return;
                }
                try {
                    await UserStore.verifyLogin();
                }
                catch (caught) {
                    await UserStore.logout({ revoke: false });
                    return;
                }
                this.lastLoginCheck = moment.utc();
                if (this.animation == null) {
                    this.renewAnimation();
                }
                if (!this.destroyed) {
                    setTimeout(() => {
                        void check();
                    }, LOGIN_CHECK_INTERVAL.asMilliseconds());
                }
            };
            void check();
        },
        /** Restore the pill content if it was tampered with. */
        restoreIfTampered() {
            const el = this.$el;
            if (el instanceof HTMLElement && el.textContent !== this.fullLabel) {
                el.textContent = this.fullLabel;
            }
        },
        /**
         * Start watching for DOM tampering.
         *
         * Note: we recreate the `MutationObserver` on every mutation so that even if Vue
         * creates a new element when restoring a tampered-with element we will observe it.
         */
        startObserver() {
            this.observer?.disconnect();
            this.observer = new MutationObserver(() => {
                this.observer?.disconnect();
                this.restoreIfTampered();
                this.startObserver();
            });
            this.observer.observe(this.$el, {
                childList: true,
                subtree: true,
                characterData: true,
                attributes: true,
            });
        },
        /** @returns Whether the last login check is older than the allowed grace period. */
        getVerificationIsExpired() {
            const maxAge = ANIMATION_CYCLE.asMilliseconds() * ANIMATION_EXPIRY_CYCLES;
            const timeSinceLastCheck = datetime
                .diff(this.lastLoginCheck, moment.utc())
                .asMilliseconds();
            return timeSinceLastCheck > maxAge;
        },
        /**
         * Start a finite shimmer animation that expires after
         * ANIMATION_EXPIRY_CYCLES cycles. Each successful login verification
         * renews it. If timers are cleared by a student, no renewal happens and
         * the animation naturally stops — making the absence of the shimmer a
         * visible signal that the session is no longer verified.
         */
        renewAnimation() {
            this.animation?.cancel();
            if (this.getVerificationIsExpired()) {
                this.animation = undefined;
                return;
            }
            const el = this.$el;
            if (!(el instanceof HTMLElement)) {
                return;
            }
            // Cancel any CSS animations so clones won't match our Web Animation
            const animations = el.getAnimations();
            animations.forEach(anim => anim.cancel());
            this.animation = el.animate([
                { backgroundPosition: '100% 0' },
                { backgroundPosition: '0 100%', offset: 0.5 },
                { backgroundPosition: '100% 0' },
            ], {
                duration: ANIMATION_CYCLE.asMilliseconds(),
                iterations: ANIMATION_EXPIRY_CYCLES,
                easing: 'linear',
            });
            /** Either clean up or renew the animation when a cycle finishes. */
            this.animation.onfinish = () => {
                if (this.destroyed || this.getVerificationIsExpired()) {
                    this.animation?.cancel();
                    this.animation = undefined;
                }
                else {
                    this.renewAnimation();
                }
            };
        },
    },
    /** @returns The rendered pill. */
    render() {
        return (<div class={[
                'cg-exam-pill label-medium',
                { 'cg-exam-pill--active': this.animation != null },
            ]}>
                {this.fullLabel}
            </div>);
    },
});
